/**
 * PromoterDashboardHeader.tsx - Promoter Dashboard Header Component
 *
 * This component displays the header section of the promoter dashboard with
 * promoter information, key metrics, and primary action buttons.
 *
 * Key Features:
 * - Promoter identification and avatar display
 * - Key performance metrics in header cards
 * - Primary action buttons (share, donate, view fundraiser)
 * - Responsive design with mobile-friendly layout
 * - Status indicators and achievement badges
 * - Navigation breadcrumbs and context
 *
 * Header Information:
 * - Promoter name and avatar/initials
 * - Fundraiser title and organization
 * - Current amount raised and goal progress
 * - Ranking position among promoters
 * - Achievement badges and status indicators
 *
 * Action Buttons:
 * - Share: Social sharing and link generation
 * - Donate: Direct donation to fundraiser
 * - View Fundraiser: Navigate to public fundraiser page
 * - Dashboard: Return to main dashboard
 *
 * Responsive Features:
 * - Mobile-optimized layout and navigation
 * - Collapsible sections for smaller screens
 * - Touch-friendly button sizing
 * - Adaptive text sizing and spacing
 *
 * Props:
 * - promoterId: string - ID of promoter for dashboard
 * - fundraiserId: string - Associated fundraiser ID
 *
 * Dependencies: MobX store, avatar generation, social sharing
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  Share2,
  Link2,
  Heart,
  Copy,
  ChevronDown,
  UserPlus,
} from "lucide-react";
import { CreateFundraiserModel, PromoterProgressModel } from "../../models";
import PreviewIcon from "../../../src/images/fundraiser-preview-icon.svg";

const HeaderContainer = styled.div<{ isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${(props) =>
    props.isScrolled ? "rgba(255, 255, 255, 0.95)" : "transparent"};
  backdrop-filter: ${(props) => (props.isScrolled ? "blur(12px)" : "none")};
  border-bottom: ${(props) =>
    props.isScrolled ? "1px solid rgba(255, 255, 255, 0.3)" : "none"};
  box-shadow: ${(props) =>
    props.isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  padding: ${(props) => (props.isScrolled ? "16px 0" : "24px 0")};
  transition: all 0.3s ease;
  width: 100%;
  margin: 0;
`;

const Header = styled.div<{ isScrolled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  min-height: ${(props) => (props.isScrolled ? "56px" : "68px")};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    min-height: ${(props) => (props.isScrolled ? "56px" : "68px")};
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const RightSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 12px;
    flex: 1;
    width: 100%;
  }
`;

const PromoterVisual = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border: 3px solid #ffffff;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PromoterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PromoterAvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e293b;
  font-weight: 800;
  font-size: 20px;
`;

const PromoterInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
`;

const PromoterName = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
`;

const StatusBadge = styled.div<{ isActive: boolean }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;

  ${(props) => {
    switch (props.isActive) {
      case true:
        return "background: #dcfce7; color: #166534;";
      case false:
        return "background: #fef3c7; color: #92400e;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const LinksDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LinksButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #1e293b;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LinksDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 180px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.isOpen ? "translateY(4px)" : "translateY(-4px)"};
  transition: all 0.2s ease;
  margin-top: 4px;
`;

const LinksDropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: #f8fafc;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ActionButton = styled.button<{ variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  white-space: nowrap;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
    }
  `
      : `
    background: rgba(255, 255, 255, 0.9);
    color: #64748b;
    border: 1px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #1e293b;
      transform: translateY(-1px);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

interface PromoterDashboardHeaderProps {
  promoter: PromoterProgressModel;
  fundraiser: CreateFundraiserModel;
  isScrolled: boolean;
  onLinksClick: () => void;
  showLinksDropdown: boolean;
  onDonateClick: () => void;
  onInviteClick: () => void;
  onShareClick: () => void;
  onCopyDonateLink: () => void;
  onCopyInviteLink: () => void;
  onCopyGalleryLink: () => void;
  onViewEmbed: () => void;
  imageError: boolean;
  onImageError: () => void;
}

export const PromoterDashboardHeader: React.FC<PromoterDashboardHeaderProps> =
  observer(
    ({
      promoter,
      fundraiser,
      isScrolled,
      onLinksClick,
      showLinksDropdown,
      onDonateClick,
      onInviteClick,
      onShareClick,
      onCopyDonateLink,
      onCopyInviteLink,
      onCopyGalleryLink,
      onViewEmbed,
      imageError,
      onImageError,
    }) => {
      const getInitials = (name: string) => {
        return name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase();
      };

      const renderPromoterVisual = () => {
        if (imageError) {
          return (
            <PromoterAvatarFallback>
              {getInitials(promoter?.displayName || "")}
            </PromoterAvatarFallback>
          );
        }

        return (
          <PromoterImage
            src={promoter?.imageURL || ""}
            alt={promoter?.displayName || ""}
            onError={onImageError}
          />
        );
      };

      return (
        <HeaderContainer isScrolled={isScrolled}>
          <Header isScrolled={isScrolled}>
            <LeftSection>
              <PromoterVisual>{renderPromoterVisual()}</PromoterVisual>

              <PromoterInfo>
                <PromoterName>{promoter.displayName}</PromoterName>
                <StatusBadge isActive={promoter.isActive}>
                  {promoter.isActive ? "Active" : "Inactive"}
                </StatusBadge>
              </PromoterInfo>
            </LeftSection>

            {promoter.isActive && (
              <RightSection>
                <LinksDropdownContainer>
                  <LinksButton
                    onClick={onLinksClick}
                    title="Important links and embed codes"
                  >
                    <Link2 size={16} />
                    Links
                    <ChevronDown size={12} />
                  </LinksButton>
                  <LinksDropdownMenu isOpen={showLinksDropdown}>
                    <LinksDropdownItem onClick={onCopyDonateLink}>
                      <span>Donate Link</span>
                      <Copy size={16} />
                    </LinksDropdownItem>
                    {fundraiser?.allowPromoterInvites && <LinksDropdownItem onClick={onCopyInviteLink}>
                      <span>Invite Link</span>
                      <Copy size={16} />
                    </LinksDropdownItem>}

                    <LinksDropdownItem onClick={onCopyGalleryLink}>
                      <span>Gallery Link</span>
                      <Copy size={16} />
                    </LinksDropdownItem>
                    <LinksDropdownItem onClick={onViewEmbed}>
                      <span>Embed Codes</span>
                      <img
                        src={PreviewIcon}
                        alt="Preview"
                        width={16}
                        height={16}
                      />
                    </LinksDropdownItem>
                  </LinksDropdownMenu>
                </LinksDropdownContainer>
                {fundraiser?.allowPromoterInvites && (
                  <ActionButton
                    onClick={onInviteClick}
                    title="Invite others to help promote"
                  >
                    {/* <img
                      src="/user-plus.svg"
                      alt="Invite"
                      width={16}
                      height={16}
                    /> */}
                    <UserPlus size={16} />
                    Invite
                  </ActionButton>
                )}
                <ActionButton onClick={onShareClick}>
                  <Share2
                    size={16}
                  //title="Share the donate page with others"
                  />
                  Share
                </ActionButton>
                <ActionButton
                  variant="primary"
                  onClick={onDonateClick}
                  title="Open the donate page"
                >
                  <Heart size={16} />
                  Donate
                </ActionButton>
              </RightSection>
            )}
          </Header>
        </HeaderContainer>
      );
    }
  );
